/**
 * Error Boundary Component
 * Catches React rendering errors and displays a user-friendly error page
 */

import React from 'react';
import { Result, Button, Typography } from 'antd';

const { Paragraph, Text } = Typography;

/**
 * Error Boundary class component
 * Must be a class component because Error Boundaries require getDerivedStateFromError
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // You could also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '24px',
          backgroundColor: '#f5f5f5',
        }}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle="We're sorry, but something unexpected happened. Please try again."
            extra={[
              <Button type="primary" key="retry" onClick={this.handleRetry}>
                Try Again
              </Button>,
              <Button key="home" onClick={this.handleGoHome}>
                Go to Home
              </Button>,
              <Button key="reload" onClick={this.handleReload}>
                Reload Page
              </Button>,
            ]}
          >
            {isDevelopment && error && (
              <div style={{ textAlign: 'left', marginTop: '24px' }}>
                <Paragraph>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    Error Details (Development Only):
                  </Text>
                </Paragraph>
                <Paragraph>
                  <pre style={{
                    backgroundColor: '#fff2f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '4px',
                    padding: '12px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    fontSize: '12px',
                  }}>
                    {error.toString()}
                    {errorInfo?.componentStack}
                  </pre>
                </Paragraph>
              </div>
            )}
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
